import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
    COURSES,
    PROGRAMMES_SECTION,
    CS_SPECIALIZATIONS,
} from "../data/home-data";
import { Button } from "./ui/button";

const DOMAIN_IMAGES = {
    "Artificial Intelligence & Machine Learning":
        "https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=2670&auto=format&fit=crop",
    "Data Science":
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2670&auto=format&fit=crop",
    "Cyber Security":
        "https://s44783.pcdn.co/in/wp-content/uploads/sites/3/2023/10/Ethical-Hacking-vs-Cyber-Security-1140x900-1-1024x808.jpg.webp",
    "Computer Science & Engineering":
        "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?q=80&w=2669&auto=format&fit=crop",
    "Information Technology":
        "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2672&auto=format&fit=crop",
};

// No green colors — using only aiblue, aipeach, aisky, aibeige, ailavender
const CARD_COLORS = [
    "bg-gradient-to-br from-ailavender to-purple-100 border-purple-200 hover:shadow-purple-200/30",
    "bg-gradient-to-br from-aipeach to-orange-100 border-orange-200 hover:shadow-orange-200/30",
    "bg-gradient-to-br from-aisky to-blue-100 border-blue-200 hover:shadow-blue-200/30",
    "bg-gradient-to-br from-aibeige to-amber-100 border-amber-200 hover:shadow-amber-200/30",
    "bg-gradient-to-br from-green-100 to-emerald-100 border-emerald-200 hover:shadow-emerald-200/30",
    "bg-gradient-to-br from-rose-100 to-pink-100 border-pink-200 hover:shadow-pink-200/30",
    "bg-gradient-to-br from-cyan-100 to-teal-100 border-teal-200 hover:shadow-teal-200/30",
];

export default function Programmes() {
    const [isPaused, setIsPaused] = useState(false);
    const [hoveredSpec, setHoveredSpec] = useState(null);
    const navigate = useNavigate();

    // Two identical copies for seamless infinite scroll
    const marqueeItems = COURSES;
    const marqueeItemsClone = COURSES;

    return (
        <section
            className="py-28 bg-white"
            data-testid="programmes-section"
            id="programmes"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
                {/* Header */}
                <div className="mb-12">
                    <p className="text-xs uppercase tracking-[0.3em] text-aiorange font-bold mb-3">
                        {PROGRAMMES_SECTION.tag}
                    </p>
                    <h2
                        className="font-display font-black text-4xl sm:text-5xl tracking-normal leading-tight max-w-xl"
                        dangerouslySetInnerHTML={{
                            __html: PROGRAMMES_SECTION.headline,
                        }}
                    />
                </div>

                {/* Flagship */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="relative rounded-[2rem] overflow-hidden p-8 sm:p-12 mb-8 bg-gradient-to-br from-aiblue via-blue-600 to-aiorange text-white grain"
                    data-testid="programme-flagship"
                >
                    <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold mb-4">
                        <Sparkles className="h-3 w-3" />{" "}
                        {PROGRAMMES_SECTION.flagship.tag}
                    </span>
                    <h3 className="font-display font-black text-3xl sm:text-5xl tracking-normal max-w-3xl">
                        {PROGRAMMES_SECTION.flagship.title}
                    </h3>
                    <p className="mt-4 text-white/90 max-w-2xl">
                        {PROGRAMMES_SECTION.flagship.description}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-6 text-sm">
                        {PROGRAMMES_SECTION.flagship.features.map((f, i) => (
                            <span key={i}>{f}</span>
                        ))}
                    </div>
                    <Button
                        onClick={() => navigate("/cs-programme")}
                        className="mt-7 bg-aiblue text-white hover:bg-blue-700 rounded-full px-6 py-5 font-semibold"
                        data-testid="flagship-cta"
                    >
                        {PROGRAMMES_SECTION.flagship.cta}{" "}
                        <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                </motion.div>

                {/* ── CS Specializations Cards ──────────────────────────────────── */}
                <div className="mt-16 mb-20">
                    <h2
                        className="font-display font-black text-3xl sm:text-4xl tracking-normal max-w-3xl mb-2"
                        dangerouslySetInnerHTML={{
                            __html: PROGRAMMES_SECTION.specializationsHeadline,
                        }}
                    />
                    <p className="mt-2 text-base text-gray-500 max-w-xl leading-relaxed mb-8">
                        {PROGRAMMES_SECTION.specializationsSubtext}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-5">
                        {[0, 1, 2].map((colIndex) => (
                            <div
                                key={colIndex}
                                className="flex-1 flex flex-col gap-5"
                            >
                                {CS_SPECIALIZATIONS.filter(
                                    (_, i) => i % 3 === colIndex,
                                ).map((spec, i) => {
                                    const originalIndex = colIndex + i * 3;
                                    const isHovered =
                                        hoveredSpec === originalIndex;
                                    return (
                                        <motion.div
                                            key={spec.name}
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{
                                                delay: originalIndex * 0.08,
                                            }}
                                            onMouseEnter={() =>
                                                setHoveredSpec(originalIndex)
                                            }
                                            onMouseLeave={() =>
                                                setHoveredSpec(null)
                                            }
                                            className={`rounded-2xl border transition-all cursor-pointer ${
                                                isHovered
                                                    ? "border-aiblue shadow-xl"
                                                    : spec.featured
                                                      ? "border-aiorange shadow-lg shadow-aiorange/10"
                                                      : "border-gray-200 hover:shadow-xl"
                                            }`}
                                        >
                                            {/* Image */}
                                            <div className="relative h-36 overflow-hidden rounded-t-2xl">
                                                <img
                                                    src={
                                                        DOMAIN_IMAGES[spec.name]
                                                    }
                                                    alt={spec.name}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                                                <div className="absolute bottom-3 left-4 right-4">
                                                    <h4 className="font-display font-bold text-sm text-white leading-tight drop-shadow-lg">
                                                        {spec.name}
                                                    </h4>
                                                </div>
                                                {spec.featured && (
                                                    <span className="absolute top-3 right-3 bg-aiorange text-black text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-md shadow-lg">
                                                        {
                                                            PROGRAMMES_SECTION.badgeMostPopular
                                                        }
                                                    </span>
                                                )}
                                            </div>

                                            {/* Static desc */}
                                            <div className="p-5 bg-white rounded-b-2xl">
                                                <p className="text-xs text-gray-500 leading-relaxed">
                                                    {spec.desc}
                                                </p>
                                            </div>

                                            {/* Expand — only pushes cards in THIS column */}
                                            <AnimatePresence>
                                                {isHovered && (
                                                    <motion.div
                                                        initial={{
                                                            height: 0,
                                                            opacity: 0,
                                                        }}
                                                        animate={{
                                                            height: "auto",
                                                            opacity: 1,
                                                        }}
                                                        exit={{
                                                            height: 0,
                                                            opacity: 0,
                                                        }}
                                                        transition={{
                                                            duration: 0.22,
                                                            ease: "easeInOut",
                                                        }}
                                                        className="overflow-hidden bg-white rounded-b-2xl"
                                                    >
                                                        <div className="px-5 pb-5 border-t border-gray-100">
                                                            <p className="text-xs text-gray-600 leading-relaxed mt-4 font-semibold">
                                                                {spec.about}
                                                            </p>
                                                            <div className="mt-4">
                                                                <Button
                                                                    onClick={(
                                                                        e,
                                                                    ) => {
                                                                        e.stopPropagation();
                                                                        navigate(
                                                                            "/waitlist?role=student",
                                                                        );
                                                                    }}
                                                                    className="bg-aiorange hover:bg-orange-600 text-black rounded-full px-6 py-4 font-semibold text-sm"
                                                                >
                                                                    {
                                                                        PROGRAMMES_SECTION.ctaJoinSpecialization
                                                                    }{" "}
                                                                    <ArrowRight className="ml-1.5 h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Courses Section Heading ───────────────────────────────────── */}
                <div id="courses" className="mt-16 mb-6 scroll-mt-28">
                    <p className="text-xs uppercase tracking-[0.3em] text-aiorange font-bold mb-3">
                        {PROGRAMMES_SECTION.coursesSection.tag}
                    </p>
                    <h3
                        className="font-display font-black text-3xl sm:text-4xl tracking-normal leading-tight max-w-xl"
                        dangerouslySetInnerHTML={{
                            __html: PROGRAMMES_SECTION.coursesSection.headline,
                        }}
                    />
                </div>
            </div>

            {/* ── Scrolling Course Marquee ──────────────────────────────────── */}
            <div className="max-w-7xl mx-auto px-4 sm:px-8 overflow-hidden relative">
                {/* Left fog mist */}
                <div
                    className="absolute left-0 top-0 bottom-0 w-24 sm:w-40 z-10 pointer-events-none"
                    style={{
                        background:
                            "linear-gradient(to right, white 20%, transparent)",
                    }}
                />
                {/* Right fog mist */}
                <div
                    className="absolute right-0 top-0 bottom-0 w-24 sm:w-40 z-10 pointer-events-none"
                    style={{
                        background:
                            "linear-gradient(to left, white 20%, transparent)",
                    }}
                />
                <div
                    className="marquee-wrapper"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                >
                    <div
                        className={`marquee-track flex gap-5 ${isPaused ? "paused" : ""}`}
                    >
                        {/* First copy */}
                        {marqueeItems.map((item, i) => {
                            const colorIdx = i % CARD_COLORS.length;
                            return (
                                <motion.div
                                    key={`a-${item.key}`}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    className={`flex-shrink-0 w-[320px] p-6 rounded-3xl border flex flex-col ${CARD_COLORS[colorIdx]} hover:shadow-xl transition-shadow`}
                                >
                                    <span className="inline-block text-xs font-bold uppercase tracking-widest text-aiblue mb-2">
                                        {item.domain}
                                    </span>
                                    <h3 className="font-display font-bold text-lg mb-2">
                                        {item.name}
                                    </h3>
                                    <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                                        {item.tagline}
                                    </p>
                                    <div className="flex flex-wrap gap-1.5 mb-3">
                                        {item.tools.slice(0, 4).map((t) => (
                                            <span
                                                key={t}
                                                className="text-xs px-2 py-1 bg-white/70 rounded-md font-medium"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="text-xs text-gray-600 space-y-1 mb-3">
                                        <div>
                                            {item.duration} · {item.level}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() =>
                                            navigate("/waitlist?role=student")
                                        }
                                        className="text-sm font-semibold text-aiblue hover:text-aiorange flex items-center gap-1 group mt-auto"
                                    >
                                        {PROGRAMMES_SECTION.ctaJoinWaitlist}{" "}
                                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </motion.div>
                            );
                        })}
                        {/* Second copy (identical — for seamless loop) */}
                        {marqueeItemsClone.map((item, i) => {
                            const colorIdx = i % CARD_COLORS.length;
                            return (
                                <motion.div
                                    key={`b-${item.key}`}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    className={`flex-shrink-0 w-[320px] p-6 rounded-3xl border flex flex-col ${CARD_COLORS[colorIdx]} hover:shadow-xl transition-shadow`}
                                >
                                    <span className="inline-block text-xs font-bold uppercase tracking-widest text-aiblue mb-2">
                                        {item.domain}
                                    </span>
                                    <h3 className="font-display font-bold text-lg mb-2">
                                        {item.name}
                                    </h3>
                                    <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                                        {item.tagline}
                                    </p>
                                    <div className="flex flex-wrap gap-1.5 mb-3">
                                        {item.tools.slice(0, 4).map((t) => (
                                            <span
                                                key={t}
                                                className="text-xs px-2 py-1 bg-white/70 rounded-md font-medium"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                    <div className="text-xs text-gray-600 space-y-1 mb-3">
                                        <div>
                                            {item.duration} · {item.level}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() =>
                                            navigate("/waitlist?role=student")
                                        }
                                        className="text-sm font-semibold text-aiblue hover:text-aiorange flex items-center gap-1 group mt-auto"
                                    >
                                        {PROGRAMMES_SECTION.ctaJoinWaitlist}{" "}
                                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Marquee Animation Styles */}
            <style>{`
        .marquee-wrapper {
          width: 100%;
        }
        .marquee-track {
          display: flex;
          width: max-content;
          animation: scroll-left 240s linear infinite;
        }
        .marquee-track.paused {
          animation-play-state: paused;
        }
        @keyframes scroll-left {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
        </section>
    );
}
